import React, { useRef } from 'react';
import { Alert, Button, DatePicker, Dropdown, Typography } from 'antd';
import { MoreOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import plLocale from 'antd/locale/pl_PL';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import type { WorkflowInvoiceIssue } from '../workflowInvoiceValidation';
import '../../../components/orders/OrderWizardShell.css';
import './InvoicePurchaseFlow.css';

interface Props {
  draft: WorkflowInvoice;
  financialOnly?: boolean;
  rateNotice?: string;
  step: number;
  setStep: (step: number) => void;
  busy: boolean;
  extracting: boolean;
  conflict: boolean;
  dirty: boolean;
  issues: WorkflowInvoiceIssue[];
  editor: React.ReactNode;
  orders: React.ReactNode;
  receipt: React.ReactNode;
  canReceive: boolean;
  showApply: boolean;
  onApply: () => void;
  onUpload: () => void;
  onExtract: () => void;
  onSave: () => void;
  onContinue: () => Promise<void>;
  onApprove: () => void;
  onClose: () => void;
  onPeriod: (period: string | null) => void;
  onDownload: (sha: string) => void;
  onDelete: () => void;
  onReload: () => void;
}

/** Presentation only. Draft, OCR, valuation and approval remain owned by InvoiceDraftDetail. */
export default function InvoicePurchaseFlow(props: Props) {
  const { t } = useTranslation(['invoices', 'common']);
  const root = useRef<HTMLDivElement>(null);
  const { draft, step, busy, extracting, conflict } = props;
  const locked = busy || extracting || conflict;
  const filePresent = Boolean(draft.sourceFileName || draft.sourceFiles?.length);
  const financialKeys = new Set(['title', 'reviewNext', 'continue', 'receipt', 'receiptTitle', 'receiptHint', 'receive']);
  const neutralKeys = new Set(['title', 'receipt', 'uploadHint', 'reviewNext', 'receiptTitle', 'receiptHint', 'continue', 'receive', 'periodNext', 'saveNext']);
  const text = (key: string) => {
    const section = props.financialOnly === true && financialKeys.has(key) ? 'financialApproval' : 'flow';
    const neutral = props.financialOnly == null && neutralKeys.has(key);
    return t(`invoices:workflow.${neutral ? 'pendingApproval' : section}.${key}`);
  };
  return <div ref={root} className="order-wizard invoice-purchase-flow" data-testid="invoice-purchase-flow">
    <header className="order-wizard__header"><h1 className="invoice-purchase-flow__title">{text('title')}</h1></header>
    <nav className="order-wizard__steps invoice-purchase-flow__steps" aria-label={text('progress')}>
      {['file', 'review', 'receipt'].map((key, index) => <div key={key} className={`order-wizard__step ${step === index ? 'is-active' : step > index ? 'is-complete' : ''}`} aria-current={step === index ? 'step' : undefined}>
        <span className="order-wizard__step-index">{index + 1}</span><span className="order-wizard__step-copy">{text(key)}</span>
      </div>)}
    </nav>
    <div className="invoice-purchase-flow__body">
      {conflict && <Alert type="error" showIcon message={t('invoices:workflow.api.versionConflict')} description={t('invoices:workflow.page.conflictRetained')} action={<Button onClick={props.onReload}>{t('common:refresh')}</Button>} />}
      <section hidden={step !== 0} className="order-wizard__section invoice-purchase-flow__card invoice-purchase-flow__intake" data-testid="invoice-intake">
        <UploadOutlined className="invoice-purchase-flow__file-icon" />
        <h2>{text('uploadTitle')}</h2>
        <p>{text('uploadHint')}</p>
        <Button type="primary" icon={<UploadOutlined />} onClick={props.onUpload} loading={busy} disabled={extracting || conflict}>{text('upload')}</Button>
        <Button data-testid="invoice-intake-manual" type="link" onClick={() => props.setStep(1)} disabled={locked}>{text('manual')}</Button>
      </section>
      <section hidden={step !== 1} className="order-wizard__section invoice-purchase-flow__card">
        <div className="invoice-purchase-flow__review-heading">
          <h2>{text('reviewTitle')}</h2>
          <Dropdown disabled={locked} menu={{ items: [{key: 'upload', label: text('addFile')}, ...(filePresent ? [{key: 'extract', label: text('retry')}] : []), ...(draft.id ? [{key: 'remove', label: t('invoices:workflow.page.deleteDraft'), danger: true}] : [])], onClick: ({key}) => { if (key === 'upload') props.onUpload(); if (key === 'extract') props.onExtract(); if (key === 'remove') props.onDelete(); } }}><Button type="text" icon={<MoreOutlined />} aria-label={text('options')} /></Dropdown>
        </div>
        <p className="invoice-purchase-flow__hint">{text('reviewNext')}</p>
        {extracting ? <Alert type="info" showIcon message={text('reading')} /> : draft.extractionStatus === 'FAILED'
          ? <Alert type="warning" showIcon message={text('failed')}
            action={<Button disabled={locked} onClick={props.onExtract}>{text('retry')}</Button>} />
          : draft.extractionStatus === 'SUCCESS' && <Typography.Paragraph type="secondary">{text('readSuccess')}</Typography.Paragraph>}
        {props.showApply && <Alert type="info" message={text('applyHint')} action={<Button disabled={locked} onClick={props.onApply}>{t('invoices:workflow.page.applyExtraction')}</Button>} />}
        {!!draft.sourceFiles?.length && <div className="invoice-purchase-flow__files">{draft.sourceFiles.map(file => <Button type="link" key={file.sha256} onClick={() => props.onDownload(file.sha256)}>{file.name}</Button>)}</div>}

        {props.rateNotice && <p className="invoice-purchase-flow__rate" role="status">{props.rateNotice}</p>}
        <div className="invoice-purchase-flow__orders">{props.orders}</div>
        {props.editor}
      </section>
      <section hidden={step !== 2} className="order-wizard__section invoice-purchase-flow__card">
        <h2>{text('receiptTitle')}</h2>
        <p className="invoice-purchase-flow__hint">{text('receiptHint')}</p>
        {props.receipt}
        <label className="invoice-purchase-flow__period">
          <span>{t('invoices:workflow.page.recognitionMonth')}</span>
          <DatePicker locale={plLocale.DatePicker} placeholder={t('invoices:workflow.page.recognitionMonth')} picker="month" aria-label={t('invoices:workflow.page.recognitionMonth')} value={draft.settlementPeriod ? dayjs(`${draft.settlementPeriod}-01`) : null} disabled={locked} onChange={date => props.onPeriod(date?.format('YYYY-MM') ?? null)} />
        </label>
        {!draft.settlementPeriod && <p role="status">{text('periodNext')}</p>}
        {props.dirty && <p role="status">{text('saveNext')}</p>}
      </section>
    </div>
    <footer className="order-wizard__actions invoice-purchase-flow__actions">
      <div className="invoice-purchase-flow__action-left"><Button onClick={props.onClose} disabled={busy || extracting}>{t('common:close')}</Button>
      {props.dirty && <span className="invoice-purchase-flow__hint">{t('invoices:workflow.summary.unsaved')}</span>}</div>
      <div className="invoice-purchase-flow__action-right">
        {step === 1 && props.issues.length > 0 && <span className="invoice-field-error" role="status">{text('missing')}</span>}
        <div className="invoice-purchase-flow__buttons">{step > 0 && <Button disabled={locked} onClick={() => props.setStep(step - 1)}>{t('common:back')}</Button>}
        {step === 1 && <Button disabled={locked} onClick={props.onSave}>{t('invoices:workflow.page.saveDraft')}</Button>}
        {step === 1 && <Button type="primary" loading={busy} disabled={extracting || conflict} onClick={() => { if (!props.issues.length) void props.onContinue(); else {
          const field = root.current?.querySelector<HTMLElement>('.invoice-review-field--invalid');
          field?.scrollIntoView({ behavior: 'auto', block: 'center' });
          field?.querySelector<HTMLElement>('input,button,textarea')?.focus({ preventScroll: true });
        } }}>{text('continue')}</Button>}
        {step === 2 && <Button type="primary" loading={busy} disabled={extracting || conflict || props.dirty || !props.canReceive || !draft.settlementPeriod} onClick={props.onApprove}>{text('receive')}</Button>}</div>
      </div>
    </footer>
  </div>;
}
